/**
 * @engine/vfx - GPU Type System
 * 
 * Fluent API for defining GPU types with WebGPU alignment and vertex format info.
 * See: https://www.w3.org/TR/WGSL/#alignment-and-size
 * 
 * @example
 * ```ts
 * // Scalar types
 * const floatType = GPUType.f32();
 * const uintType = GPUType.u32();
 * 
 * // Vector types via builder
 * const vec3 = GPUType.f32().vec3();
 * const vec4u = GPUType.u32().vec4();
 * 
 * // Atomics
 * const atomicU32 = GPUType.u32().atomic();
 * 
 * // Matrices
 * const mat4 = GPUType.f32().mat4x4();
 * 
 * // Define struct with fluent types
 * const Particle = defineStruct('Particle', {
 *   position: GPUType.f32().vec3(),
 *   lifetime: GPUType.f32(),
 *   color: GPUType.f32().vec4(),
 *   alive: GPUType.u32(),
 * });
 * 
 * // Get vertex attributes for pipeline
 * const attrs = Particle.vertexAttrs({
 *   position: 0,
 *   color: 2,
 *   alive: 3,
 * });
 * ```
 */

// ============================================================================
// GPU VERTEX FORMATS
// ============================================================================

/**
 * WebGPU vertex formats.
 * @see https://www.w3.org/TR/webgpu/#enumdef-gpuvertexformat
 */
export type GPUVertexFormat =
  | 'uint8x2' | 'uint8x4'
  | 'sint8x2' | 'sint8x4'
  | 'unorm8x2' | 'unorm8x4'
  | 'snorm8x2' | 'snorm8x4'
  | 'uint16x2' | 'uint16x4'
  | 'sint16x2' | 'sint16x4'
  | 'unorm16x2' | 'unorm16x4'
  | 'snorm16x2' | 'snorm16x4'
  | 'float16x2' | 'float16x4'
  | 'float32' | 'float32x2' | 'float32x3' | 'float32x4'
  | 'uint32' | 'uint32x2' | 'uint32x3' | 'uint32x4'
  | 'sint32' | 'sint32x2' | 'sint32x3' | 'sint32x4'
  | 'unorm10-10-10-2';

// ============================================================================
// TYPE INFO
// ============================================================================

/**
 * Complete GPU type information.
 */
export interface GPUTypeInfo {
  /** WGSL type string (e.g. 'vec3<f32>') */
  readonly wgsl: string;
  /** Size in bytes */
  readonly size: number;
  /** Alignment in bytes (WebGPU spec) */
  readonly align: number;
  /** WebGPU vertex format, or null if not usable as vertex attribute */
  readonly vertex: GPUVertexFormat | null;
  /** Base scalar type */
  readonly scalar: 'f32' | 'u32' | 'i32' | 'f16' | 'bool';
  /** Number of components (1 for scalar, 2-4 for vectors, 4-16 for matrices) */
  readonly components: number;
  /** True if this is an atomic type */
  readonly isAtomic: boolean;
  /** True if this is a matrix type */
  readonly isMatrix: boolean;
}

// ============================================================================
// TYPE BUILDER
// ============================================================================

/**
 * Builder for scalar types - can be converted to vectors, matrices, or atomics.
 */
export class ScalarTypeBuilder {
  private readonly _scalar: 'f32' | 'u32' | 'i32' | 'f16' | 'bool';
  
  constructor(scalar: 'f32' | 'u32' | 'i32' | 'f16' | 'bool') {
    this._scalar = scalar;
  }
  
  /**
   * Get the scalar type info.
   */
  build(): GPUTypeInfo {
    return SCALAR_TYPES[this._scalar];
  }
  
  /**
   * Convert to vec2.
   */
  vec2(): GPUTypeInfo {
    return VEC2_TYPES[this._scalar] ?? throwUnsupported(`vec2<${this._scalar}>`);
  }
  
  /**
   * Convert to vec3.
   */
  vec3(): GPUTypeInfo {
    return VEC3_TYPES[this._scalar] ?? throwUnsupported(`vec3<${this._scalar}>`);
  }
  
  /**
   * Convert to vec4.
   */
  vec4(): GPUTypeInfo {
    return VEC4_TYPES[this._scalar] ?? throwUnsupported(`vec4<${this._scalar}>`);
  }
  
  /**
   * Convert to atomic (u32 and i32 only).
   */
  atomic(): GPUTypeInfo {
    if (this._scalar !== 'u32' && this._scalar !== 'i32') {
      throw new Error(`atomic<${this._scalar}> is not supported, only atomic<u32> and atomic<i32>`);
    }
    return ATOMIC_TYPES[this._scalar]!;
  }
  
  /**
   * Convert to 2x2 matrix (f32 only).
   */
  mat2x2(): GPUTypeInfo {
    if (this._scalar !== 'f32') {
      throw new Error(`mat2x2<${this._scalar}> is not supported, only mat2x2<f32>`);
    }
    return MATRIX_TYPES.mat2x2f;
  }
  
  /**
   * Convert to 3x3 matrix (f32 only).
   */
  mat3x3(): GPUTypeInfo {
    if (this._scalar !== 'f32') {
      throw new Error(`mat3x3<${this._scalar}> is not supported, only mat3x3<f32>`);
    }
    return MATRIX_TYPES.mat3x3f;
  }
  
  /**
   * Convert to 4x4 matrix (f32 only).
   */
  mat4x4(): GPUTypeInfo {
    if (this._scalar !== 'f32') {
      throw new Error(`mat4x4<${this._scalar}> is not supported, only mat4x4<f32>`);
    }
    return MATRIX_TYPES.mat4x4f;
  }
}

function throwUnsupported(type: string): never {
  throw new Error(`GPU type ${type} is not supported`);
}

// ============================================================================
// TYPE CONSTANTS
// ============================================================================

const SCALAR_TYPES: Record<string, GPUTypeInfo> = {
  f32:  { wgsl: 'f32',  size: 4, align: 4, vertex: 'float32', scalar: 'f32',  components: 1, isAtomic: false, isMatrix: false },
  u32:  { wgsl: 'u32',  size: 4, align: 4, vertex: 'uint32',  scalar: 'u32',  components: 1, isAtomic: false, isMatrix: false },
  i32:  { wgsl: 'i32',  size: 4, align: 4, vertex: 'sint32',  scalar: 'i32',  components: 1, isAtomic: false, isMatrix: false },
  f16:  { wgsl: 'f16',  size: 2, align: 2, vertex: null,      scalar: 'f16',  components: 1, isAtomic: false, isMatrix: false },
  bool: { wgsl: 'bool', size: 4, align: 4, vertex: null,      scalar: 'bool', components: 1, isAtomic: false, isMatrix: false },
};

const VEC2_TYPES: Record<string, GPUTypeInfo> = {
  f32: { wgsl: 'vec2<f32>', size: 8,  align: 8,  vertex: 'float32x2', scalar: 'f32', components: 2, isAtomic: false, isMatrix: false },
  u32: { wgsl: 'vec2<u32>', size: 8,  align: 8,  vertex: 'uint32x2',  scalar: 'u32', components: 2, isAtomic: false, isMatrix: false },
  i32: { wgsl: 'vec2<i32>', size: 8,  align: 8,  vertex: 'sint32x2',  scalar: 'i32', components: 2, isAtomic: false, isMatrix: false },
  f16: { wgsl: 'vec2<f16>', size: 4,  align: 4,  vertex: 'float16x2', scalar: 'f16', components: 2, isAtomic: false, isMatrix: false },
};

const VEC3_TYPES: Record<string, GPUTypeInfo> = {
  f32: { wgsl: 'vec3<f32>', size: 12, align: 16, vertex: 'float32x3', scalar: 'f32', components: 3, isAtomic: false, isMatrix: false },
  u32: { wgsl: 'vec3<u32>', size: 12, align: 16, vertex: 'uint32x3',  scalar: 'u32', components: 3, isAtomic: false, isMatrix: false },
  i32: { wgsl: 'vec3<i32>', size: 12, align: 16, vertex: 'sint32x3',  scalar: 'i32', components: 3, isAtomic: false, isMatrix: false },
};

const VEC4_TYPES: Record<string, GPUTypeInfo> = {
  f32: { wgsl: 'vec4<f32>', size: 16, align: 16, vertex: 'float32x4', scalar: 'f32', components: 4, isAtomic: false, isMatrix: false },
  u32: { wgsl: 'vec4<u32>', size: 16, align: 16, vertex: 'uint32x4',  scalar: 'u32', components: 4, isAtomic: false, isMatrix: false },
  i32: { wgsl: 'vec4<i32>', size: 16, align: 16, vertex: 'sint32x4',  scalar: 'i32', components: 4, isAtomic: false, isMatrix: false },
};

const ATOMIC_TYPES: Record<string, GPUTypeInfo> = {
  u32: { wgsl: 'atomic<u32>', size: 4, align: 4, vertex: null, scalar: 'u32', components: 1, isAtomic: true, isMatrix: false },
  i32: { wgsl: 'atomic<i32>', size: 4, align: 4, vertex: null, scalar: 'i32', components: 1, isAtomic: true, isMatrix: false },
};

const MATRIX_TYPES = {
  mat2x2f: { wgsl: 'mat2x2<f32>', size: 16, align: 8,  vertex: null, scalar: 'f32' as const, components: 4,  isAtomic: false, isMatrix: true },
  mat3x3f: { wgsl: 'mat3x3<f32>', size: 48, align: 16, vertex: null, scalar: 'f32' as const, components: 12, isAtomic: false, isMatrix: true },
  mat4x4f: { wgsl: 'mat4x4<f32>', size: 64, align: 16, vertex: null, scalar: 'f32' as const, components: 16, isAtomic: false, isMatrix: true },
};

// ============================================================================
// GPU TYPE API
// ============================================================================

/**
 * GPU Type builder entry point.
 * 
 * @example
 * ```ts
 * GPUType.f32()           // f32 scalar
 * GPUType.f32().vec3()    // vec3<f32>
 * GPUType.u32().atomic()  // atomic<u32>
 * GPUType.f32().mat4x4()  // mat4x4<f32>
 * ```
 */
export const GPUType = {
  /** 32-bit float */
  f32: () => new ScalarTypeBuilder('f32'),
  /** 32-bit unsigned integer */
  u32: () => new ScalarTypeBuilder('u32'),
  /** 32-bit signed integer */
  i32: () => new ScalarTypeBuilder('i32'),
  /** 16-bit float (requires f16 extension) */
  f16: () => new ScalarTypeBuilder('f16'),
  /** Boolean (4 bytes in WGSL) */
  bool: () => new ScalarTypeBuilder('bool'),
} as const;

// ============================================================================
// STRUCT DEFINITION
// ============================================================================

/**
 * Field definition in a struct.
 */
export interface StructFieldDef {
  /** Field name */
  name: string;
  /** GPU type info */
  type: GPUTypeInfo;
  /** Calculated byte offset */
  offset: number;
}

/**
 * Vertex attribute definition for pipeline.
 */
export interface VertexAttribute {
  name: string;
  format: GPUVertexFormat;
  offset: number;
  location: number;
}

/**
 * Complete vertex buffer layout for pipeline.
 */
export interface VertexBufferLayout {
  stride: number;
  stepMode: 'vertex' | 'instance';
  attributes: VertexAttribute[];
}

/**
 * Struct definition with all computed metadata.
 */
export interface StructDef<TFields extends Record<string, GPUTypeInfo>> {
  /** Struct name */
  readonly name: string;
  /** Total size in bytes (including padding) */
  readonly size: number;
  /** Struct alignment */
  readonly align: number;
  /** Field definitions with offsets */
  readonly fields: readonly StructFieldDef[];
  /** Quick lookup: field name → offset */
  readonly offsets: { readonly [K in keyof TFields]: number };
  /** Quick lookup: field name → type info */
  readonly types: TFields;
  
  /**
   * Get byte offset for a field.
   */
  offset<K extends keyof TFields>(field: K): number;
  
  /**
   * Generate vertex attributes for selected fields.
   * @param locations - Map of field name to shader location
   * @param stepMode - 'vertex' or 'instance' (default: 'instance')
   */
  vertexAttrs(
    locations: Partial<{ [K in keyof TFields]: number }>,
    stepMode?: 'vertex' | 'instance'
  ): VertexBufferLayout;
  
  /**
   * Generate WGSL struct definition.
   */
  toWGSL(): string;
  
  /**
   * Create a StructWriter for this struct.
   */
  createWriter(): StructWriter<TFields>;
}

// ============================================================================
// STRUCT WRITER
// ============================================================================

/**
 * Type-safe writer for struct buffers.
 */
export class StructWriter<TFields extends Record<string, GPUTypeInfo>> {
  private readonly _def: StructDef<TFields>;
  private readonly _buffer: ArrayBuffer;
  private readonly _f32: Float32Array;
  private readonly _u32: Uint32Array;
  private readonly _i32: Int32Array;
  
  constructor(def: StructDef<TFields>) {
    this._def = def;
    this._buffer = new ArrayBuffer(def.size);
    this._f32 = new Float32Array(this._buffer);
    this._u32 = new Uint32Array(this._buffer);
    this._i32 = new Int32Array(this._buffer);
  }
  
  get buffer(): ArrayBuffer { return this._buffer; }
  get float32(): Float32Array { return this._f32; }
  get uint32(): Uint32Array { return this._u32; }
  get size(): number { return this._def.size; }
  
  set<K extends keyof TFields & string>(name: K, value: GPUValue): this {
    const field = this._def.fields.find(f => f.name === name);
    if (!field) {
      throw new Error(`Unknown field: ${name} in struct ${this._def.name}`);
    }
    
    const idx = field.offset / 4;
    const type = field.type;
    
    if (type.isMatrix) {
      this._writeMatrix(idx, value as Float32Array | number[], type.components);
    } else if (type.components === 1) {
      this._writeScalar(idx, value as number, type);
    } else {
      this._writeVector(idx, value as VecLike, type);
    }
    
    return this;
  }
  
  /**
   * Clear buffer to zeros.
   */
  clear(): this {
    this._f32.fill(0);
    return this;
  }
  
  /**
   * Set multiple field values at once.
   */
  setAll(values: { [K in keyof TFields]?: GPUValue }): this {
    for (const [name, value] of Object.entries(values)) {
      if (value !== undefined) {
        this.set(name as keyof TFields & string, value);
      }
    }
    return this;
  }
  
  private _writeScalar(idx: number, v: number, type: GPUTypeInfo): void {
    if (type.scalar === 'f32' || type.scalar === 'f16' || type.scalar === 'bool') {
      this._f32[idx] = v;
    } else if (type.scalar === 'u32') {
      this._assertU32(v);
      this._u32[idx] = v;
    } else {
      this._assertI32(v);
      this._i32[idx] = v;
    }
  }
  
  private _writeVector(idx: number, v: VecLike | number[] | Float32Array, type: GPUTypeInfo): void {
    const isUint = type.scalar === 'u32';
    const isInt = type.scalar === 'i32';
    const view = isUint ? this._u32 : isInt ? this._i32 : this._f32;
    
    // Handle arrays (number[] or Float32Array)
    if (Array.isArray(v) || v instanceof Float32Array) {
      for (let i = 0; i < type.components && i < v.length; i++) {
        if (isUint) this._assertU32(v[i]);
        else if (isInt) this._assertI32(v[i]);
        view[idx + i] = v[i];
      }
      return;
    }
    
    // Handle VecLike objects
    if (isUint) {
      this._assertU32(v.x);
      this._assertU32(v.y);
    } else if (isInt) {
      this._assertI32(v.x);
      this._assertI32(v.y);
    }
    
    view[idx] = v.x;
    view[idx + 1] = v.y;
    
    if (type.components >= 3 && 'z' in v) {
      if (isUint) this._assertU32(v.z!);
      else if (isInt) this._assertI32(v.z!);
      view[idx + 2] = v.z!;
    }
    if (type.components >= 4 && 'w' in v) {
      if (isUint) this._assertU32(v.w!);
      else if (isInt) this._assertI32(v.w!);
      view[idx + 3] = v.w!;
    }
  }
  
  private _writeMatrix(idx: number, m: Float32Array | number[], len: number): void {
    for (let i = 0; i < len; i++) {
      this._f32[idx + i] = m[i];
    }
  }
  
  private _assertU32(v: number): void {
    if (!Number.isInteger(v) || v < 0 || v > 0xFFFFFFFF) {
      throw new RangeError(`u32 must be in [0, ${0xFFFFFFFF}], got ${v}`);
    }
  }
  
  private _assertI32(v: number): void {
    if (!Number.isInteger(v) || v < -0x80000000 || v > 0x7FFFFFFF) {
      throw new RangeError(`i32 must be in [${-0x80000000}, ${0x7FFFFFFF}], got ${v}`);
    }
  }
}

// ============================================================================
// VALUE TYPES
// ============================================================================

interface VecLike { x: number; y: number; z?: number; w?: number }

/**
 * All valid GPU value types for StructWriter.
 */
export type GPUValue = 
  | number 
  | { x: number; y: number } 
  | { x: number; y: number; z: number } 
  | { x: number; y: number; z: number; w: number }
  | Float32Array 
  | number[];

// ============================================================================
// DEFINE STRUCT
// ============================================================================

/**
 * Align value to alignment boundary.
 */
function alignTo(value: number, alignment: number): number {
  return Math.ceil(value / alignment) * alignment;
}

/**
 * Define a GPU struct with automatic layout calculation.
 * 
 * @param name - Struct name (used in WGSL generation)
 * @param fields - Object mapping field names to GPU types
 * @returns Complete struct definition with utilities
 * 
 * @example
 * ```ts
 * const Particle = defineStruct('Particle', {
 *   position: GPUType.f32().vec3(),
 *   lifetime: GPUType.f32().build(),
 *   color: GPUType.f32().vec4(),
 *   alive: GPUType.u32().build(),
 * });
 * 
 * console.log(Particle.size);           // Total size
 * console.log(Particle.offset('color')); // Byte offset
 * console.log(Particle.toWGSL());       // WGSL struct code
 * ```
 */
export function defineStruct<TFields extends Record<string, GPUTypeInfo>>(
  name: string,
  fields: TFields
): StructDef<TFields> {
  const fieldDefs: StructFieldDef[] = [];
  const offsets = {} as { [K in keyof TFields]: number };
  
  let currentOffset = 0;
  let maxAlign = 0;
  
  for (const [fieldName, typeInfo] of Object.entries(fields)) {
    // Align to field alignment
    const alignedOffset = alignTo(currentOffset, typeInfo.align);
    
    fieldDefs.push({
      name: fieldName,
      type: typeInfo,
      offset: alignedOffset,
    });
    
    (offsets as Record<string, number>)[fieldName] = alignedOffset;
    
    currentOffset = alignedOffset + typeInfo.size;
    maxAlign = Math.max(maxAlign, typeInfo.align);
  }
  
  // Pad struct size to alignment
  const structSize = alignTo(currentOffset, maxAlign);
  
  const def: StructDef<TFields> = {
    name,
    size: structSize,
    align: maxAlign,
    fields: fieldDefs,
    offsets,
    types: fields,
    
    offset(field) {
      return offsets[field];
    },
    
    vertexAttrs(locations, stepMode = 'instance') {
      const attrs: VertexAttribute[] = [];
      
      for (const [fieldName, location] of Object.entries(locations)) {
        if (location === undefined) continue;
        
        const field = fieldDefs.find(f => f.name === fieldName);
        if (!field) {
          throw new Error(`Unknown field: ${fieldName} in struct ${name}`);
        }
        
        if (!field.type.vertex) {
          throw new Error(`Field ${fieldName} (${field.type.wgsl}) cannot be used as vertex attribute`);
        }
        
        attrs.push({
          name: fieldName,
          format: field.type.vertex,
          offset: field.offset,
          location,
        });
      }
      
      // Sort by location for consistent output
      attrs.sort((a, b) => a.location - b.location);
      
      return {
        stride: structSize,
        stepMode,
        attributes: attrs,
      };
    },
    
    toWGSL() {
      const lines = [`struct ${name} {`];
      for (const field of fieldDefs) {
        lines.push(`  ${field.name}: ${field.type.wgsl},`);
      }
      lines.push('}');
      return lines.join('\n');
    },
    
    createWriter() {
      return new StructWriter(def);
    },
  };
  
  return def;
}

// ============================================================================
// PRETTY PRINT
// ============================================================================

/**
 * Print struct layout for debugging.
 */
export function printStructLayout(def: StructDef<Record<string, GPUTypeInfo>>): string {
  const lines: string[] = [
    `struct ${def.name} (${def.size} bytes, align ${def.align})`,
    '─'.repeat(50),
  ];
  
  for (const field of def.fields) {
    const offsetHex = `0x${field.offset.toString(16).padStart(2, '0')}`;
    lines.push(
      `  ${offsetHex} │ ${field.name.padEnd(20)} │ ${field.type.wgsl.padEnd(15)} │ ${field.type.size}b`
    );
  }
  
  lines.push('─'.repeat(50));
  return lines.join('\n');
}

/**
 * Create a struct writer (backward compatible factory).
 * The generic T is for type hinting - pass the data interface for better autocomplete.
 */
export function createStructWriter<T = Record<string, unknown>>(
  layout: StructDef<Record<string, GPUTypeInfo>>
): StructWriter<Record<string, GPUTypeInfo>> {
  return new StructWriter(layout);
}
